import React from "react";
import useApiService from "../../../hooks/useApiService";
import { getAllClassifieds } from "../../../services/main-service/mainService";
import { useSearchParams } from "react-router-dom";
import DisplayAdsSection from "../display-ads-section/DisplayAdsSection";
import {
  AD_LIST_PAGE_ADS_LIMIT,
  AD_LIST_PAGE_BOOSTED_LIMIT,
  AD_LIST_PAGE_PREMIUM_LIMIT,
} from "../../../configs/limits/adLimits";
import Spinner from "../../common/spinner/Spinner";
import PriceFilter from "../advanced-filters/price-filter/PriceFilter";
import ThirdPartyAdSpace from "../../common/third-party-ad-space/ThirdPartyAdSpace";
import { BannerSpace } from "../../../types/admin-types/adminTypes";

interface AdListingSectionProps {
  leftBanner?:    BannerSpace;
  sidebarBanner?: BannerSpace;
}

function AdListingSection({ leftBanner, sidebarBanner }: AdListingSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const page       = searchParams.get("page")       || "";
  const categoryType = searchParams.get("categoryType") || "";
  const location   = searchParams.get("location")   || "";
  const searchTerm = searchParams.get("searchTerm") || "";
  const min        = searchParams.get("min")        || "";
  const max        = searchParams.get("max")        || "";

  const payload = {
    ad_limit:         AD_LIST_PAGE_ADS_LIMIT,
    ad_skip:          0 + (Number(page) - 1) * AD_LIST_PAGE_ADS_LIMIT,
    boosted_ad_limit: AD_LIST_PAGE_BOOSTED_LIMIT,
    category:         categoryType,
    location:         location,
    premium_ad_limit: AD_LIST_PAGE_PREMIUM_LIMIT,
    search_term:      searchTerm.replaceAll("+", " "),
    seller_id:        "",
    min_price:        min !== "" ? Number(min) : null,
    max_price:        max !== "" ? Number(max) : null,
  };

  const { data, loading, error } = useApiService({
    serviceCall: () => getAllClassifieds(payload),
    dependencies: [page, searchTerm, categoryType, location, min, max],
  });

  function handleNextBtn() {
    const newParams: any = { page: (Number(page) + 1).toString() };
    if (categoryType) newParams.categoryType = categoryType;
    if (location)     newParams.location     = location;
    if (searchTerm)   newParams.searchTerm   = searchTerm;
    setSearchParams(newParams);
  }

  function handlePreviousBtn() {
    if (Number(page) - 1 > 0) {
      const newParams: any = { page: (Number(page) - 1).toString() };
      if (categoryType) newParams.categoryType = categoryType;
      if (location)     newParams.location     = location;
      if (searchTerm)   newParams.searchTerm   = searchTerm;
      setSearchParams(newParams);
    }
  }

  if (loading) return <Spinner />;
  if (error)   return <p className="text-center">{error}</p>;

  const hasLeftBanner    = (leftBanner?.ad_presigned_urls?.length    ?? 0) > 0;
  const hasSidebarBanner = (sidebarBanner?.ad_presigned_urls?.length ?? 0) > 0;

  // column widths: left=3, right=2 (if active), middle gets the rest
  const middleCols = 12 - 3 - (hasSidebarBanner ? 2 : 0);

  return (
    <div className="row g-0">

      {/* Left column — price filter + optional banner */}
      <div className="col-md-3 d-none d-md-block" style={{ borderRight: "1px solid #f1f5f9", padding: "1.5rem 1.25rem" }}>
        <PriceFilter />
        {hasLeftBanner && (
          <div style={{ marginTop: "1.5rem" }}>
            <ThirdPartyAdSpace
              urls={leftBanner!.ad_presigned_urls}
              aspectRatio={leftBanner!.resolution}
            />
          </div>
        )}
      </div>

      {/* Middle column — ads */}
      <div className={`col-sm-12 col-md-${middleCols}`} style={{ padding: "1.25rem 1rem" }}>
        <DisplayAdsSection
          premiumAdList={data?.premium_ads ?? []}
          boostedAdList={data?.boosted_ads ?? []}
          adList={data?.ads ?? []}
          isLastPage={data?.is_ads_last_page ?? false}
          handleNextBtn={handleNextBtn}
          handlePreviousBtn={handlePreviousBtn}
          pageNo={Number(page)}
        />
      </div>

      {/* Right column — sidebar banner */}
      {hasSidebarBanner && (
        <div className="col-md-2 d-none d-md-block" style={{ borderLeft: "1px solid #f1f5f9", padding: "1.5rem 0.75rem" }}>
          <ThirdPartyAdSpace
            urls={sidebarBanner!.ad_presigned_urls}
            aspectRatio={sidebarBanner!.resolution}
          />
        </div>
      )}

    </div>
  );
}

export default AdListingSection;
